'use client';

import { useEffect, useMemo, useState } from 'react';
import { type UseAutoUpdaterReturn } from '../hooks/useAutoUpdater';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Button, LegacyDialog, Progress, Text } from '@terra/ui-design-system';
import {
  CircleArrowUp02Icon,
  Download01Icon,
  RefreshIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { AUTO_UPDATER_OPEN_DIALOG_EVENT } from '../constants/auto-updater';
import { useAutoUpdaterPreferencesStore } from '../store/autoUpdaterPreferencesStore';

type UpdateNotificationProps = {
  autoUpdater: UseAutoUpdaterReturn;
};

export function UpdateNotification({ autoUpdater }: UpdateNotificationProps) {
  const t = useTranslations('autoUpdater');
  const {
    updateState,
    updateInfo,
    downloadProgress,
    downloadUpdate,
    installUpdate,
    hasPendingUpdate,
    shouldShowUpdateModal,
    isForceUpdate,
    remindLater,
    dismissForSession,
    resetSessionDismiss,
    clearSnooze,
  } = autoUpdater;
  const [isOpen, setIsOpen] = useState(false);
  const setDialogOpenedByUser = useAutoUpdaterPreferencesStore(
    (state) => state.setDialogOpenedByUser,
  );

  useEffect(() => {
    if (shouldShowUpdateModal) {
      setIsOpen(true);

      return;
    }

    setIsOpen(false);
  }, [shouldShowUpdateModal]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const openDialog = () => {
      setDialogOpenedByUser({ openedByUser: true });
      clearSnooze();
      resetSessionDismiss();
      setIsOpen(true);
    };

    window.addEventListener(AUTO_UPDATER_OPEN_DIALOG_EVENT, openDialog);

    return () => {
      window.removeEventListener(AUTO_UPDATER_OPEN_DIALOG_EVENT, openDialog);
    };
  }, [clearSnooze, setDialogOpenedByUser, resetSessionDismiss]);
  const handleOpenChange = ({ open }: { open: boolean }) => {
    if (isForceUpdate && !open) {
      return;
    }

    setIsOpen(open);
    if (!open) {
      dismissForSession();
    }
  };

  const handleRemindLater = () => {
    remindLater();
    setIsOpen(false);
  };

  const isPreparingDownload = updateState === 'preparing-download';
  const title = useMemo(() => {
    if (updateState === 'preparing-download') {
      return t('startingDownload');
    }

    if (updateState === 'downloading') {
      return t('downloadingTitle');
    }

    if (updateState === 'downloaded') {
      return t('readyToInstallTitle');
    }

    if (isForceUpdate) {
      return t('forceTitle');
    }

    return t('availableTitle');
  }, [isForceUpdate, t, updateState]);
  const releaseNotes =
    typeof updateInfo?.releaseNotes === 'string'
      ? updateInfo.releaseNotes
      : null;

  if (!hasPendingUpdate) {
    return null;
  }

  const showRemindLater =
    !isForceUpdate &&
    (updateState === 'available' || updateState === 'downloaded');

  return (
    <LegacyDialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="md">
          <VStack gap="4" p="4">
            <VStack gap="1" alignItems="flex-start" w="full">
              <HStack gap="2" alignItems="center">
                <Box color="fg.muted" display="inline-flex">
                  {updateState === 'downloaded' ? (
                    <RefreshIcon size={14} />
                  ) : updateState === 'downloading' || isPreparingDownload ? (
                    <Download01Icon size={14} />
                  ) : (
                    <CircleArrowUp02Icon size={14} />
                  )}
                </Box>
                <LegacyDialog.Title>{title}</LegacyDialog.Title>
              </HStack>
              <Text fontSize="xs" color="fg.muted">
                {updateInfo?.version
                  ? `${t('versionLine', { version: updateInfo.version })} — `
                  : ''}
                {updateState === 'downloaded'
                  ? t('readyDescription')
                  : isPreparingDownload
                    ? t('startingDownloadDescription')
                    : t('availableDescription')}
              </Text>
              {isForceUpdate ? (
                <Text fontSize="xs" color="fg.warning">
                  {t('forceUpdateExplanation')}
                </Text>
              ) : null}
            </VStack>

            {releaseNotes ? (
              <Box
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="sm"
                bg="bg.subtle"
                p="2"
                maxH="80px"
                overflowY="auto"
                w="full"
              >
                <Text fontSize="xs" color="fg.muted">
                  {releaseNotes}
                </Text>
              </Box>
            ) : null}

            {updateState === 'downloading' && downloadProgress ? (
              <VStack gap="1" alignItems="stretch" w="full">
                <Progress value={downloadProgress.percent} size="sm" />
                <HStack justify="space-between">
                  <Text fontSize="xs" color="fg.muted">
                    {t('downloadPercent', {
                      percent: downloadProgress.percent.toFixed(0),
                    })}
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    {t('downloadSize', {
                      transferred: (
                        downloadProgress.transferred /
                        1024 /
                        1024
                      ).toFixed(1),
                      total: (downloadProgress.total / 1024 / 1024).toFixed(1),
                    })}
                  </Text>
                </HStack>
              </VStack>
            ) : null}

            <HStack gap="2" justify="flex-end" w="full">
              {showRemindLater ? (
                <Button variant="outline" size="xs" onClick={handleRemindLater}>
                  {t('remindMeLater')}
                </Button>
              ) : null}

              {updateState === 'available' || isPreparingDownload ? (
                <Button
                  size="xs"
                  loading={isPreparingDownload}
                  disabled={isPreparingDownload}
                  loadingText={
                    isPreparingDownload ? t('startingDownload') : undefined
                  }
                  aria-label={
                    isPreparingDownload
                      ? t('startingDownload')
                      : t('downloadNow')
                  }
                  onClick={downloadUpdate}
                >
                  {t('downloadNow')}
                </Button>
              ) : null}

              {updateState === 'downloaded' ? (
                <Button size="xs" onClick={installUpdate}>
                  {t('restartAndInstall')}
                </Button>
              ) : null}
            </HStack>
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
